// BubbleChat.js - Enhanced with Blocking Handshake Protocol

import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { generateBubbleChat } from "../apis";
import "../style.css";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import LMSStyle from "./LMSStyle";
import HeygenStyle from "./HeygenStyle";
import DefaultStyle from "./DefaultStyle";
import LMSQuizStyle from "./LMSQuizStyle";
import LMSActivityStyle from "./LMSActivityStyle";
import WaveStyle from "./WaveStyle";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import IYKYKFastInline from "./IYKYKFastInline";
import HybridChatInterface from "./HybridChatInterface";

// ElevenLabs API Configuration
const ELEVENLABS_API_KEY =
  "sk_41c4c2aed46d1f7b9a2cd1f4a1f1dc5cb666e91cc46102a0";
const ELEVENLABS_API_URL = "https://api.elevenlabs.io/v1";

// Voice IDs for different characters
const ELEVENLABS_VOICE_MAP = {
  default: "j1R16HPdHkTljfMFjWBs",
  ren: "wJH1bZmBUIfpUYrl489F",
  ivy: "DyRgVG0XuMGmW5oj528s",
  felix: "x9qnEOK2bn2vJQDYxfFe",
  gold: "j1R16HPdHkTljfMFjWBs",
  alex: "O0P5ADT4XwluX8kXUii0",
  kel: "5qU6236omKrtPYmOAitO",
};

const ELEVENLABS_MODEL = "eleven_flash_v2_5";

// HeyGen TTS and avatar streaming constants
const HEYGEN_API_KEY =
  "OTQ2MzIzMmNkM2U1NDAzODk2OGRhNTU3MWY1NDAzYTMtMTczNDExMDc3OQ==";
const HEYGEN_SERVER_URL = "https://api.heygen.com";
const HEYGEN_AVATAR_ID = "ef08039a41354ed5a20565db899373f3";
const HEYGEN_VOICE_ID = "1bd001e7e50f421d891986aad5158bc8";

// Speech SDK for voice recognition
const SPEECH_KEY =
  "4qJDg7p3eSOWexOkXWFz8b1Zh2uSTmlN8X3iZu9ZH6YTiubpaBmTJQQJ99BCACYeBjFXJ3w3AAAYACOGfDLx";
const SPEECH_REGION = "eastus";

function BubbleChat({
  activeChat,
  setActiveChat,
  setQueries,
  questionList,
  setQuestionList,
  avatar,
  session_id,
  class_id,
}) {
  const serverAddress = process.env.REACT_APP_URL;
  const activeModel = useSelector((store) => store.auth.activeModel);
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef(null);
  const timestampedQuestions = useRef([]);
  const location = useLocation();
  const [initialMessage, setInitialMessage] = useState(
    "System Message: Please continue the user where they have left off or introduce them into the lesson as if they haven't sent any messages prior to this."
  );

  // Style states
  const [isLMSStyle, setIsLMSStyle] = useState(false);
  const [isHeygenStyle, setIsHeygenStyle] = useState(false);
  const [isLMSQuizStyle, setIsLMSQuizStyle] = useState(false);
  const [isLMSActivityStyle, setIsLMSActivityStyle] = useState(false);
  const [isLMSQuizStyle2, setIsLMSQuizStyle2] = useState(false);
  const [isLMSQuizStyle3, setIsLMSQuizStyle3] = useState(false);
  const [isWaveStyle, setIsWaveStyle] = useState(false);
  const [isIYKYKFastStyle, setIsIYKYKFastStyle] = useState(false);
  const [isHybridStyle, setIsHybridStyle] = useState(false); // Add this new state
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [contextRequired, setContextRequired] = useState(false);

  // BLOCKING HANDSHAKE STATE
  const [handshakeState, setHandshakeState] = useState({
    isReady: false,
    userDataReceived: false,
    handshakeComplete: false,
    isBlocked: false, // Changed from true to false initially
    errorMessage: null,
    loadingMessage: null, // Changed from 'Initializing AI Assistant...' to null
  });

  // TalentLMS Integration
  const [talentLmsData, setTalentLmsData] = useState(null);
  const [userAccountContext, setUserAccountContext] = useState(null);
  const talentLmsDataRef = useRef(null);
  const userContextRef = useRef(null);

  // Typewriter effect states
  const [displayedText, setDisplayedText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [skipTyping, setSkipTyping] = useState(false);

  // TTS enabled or not
  const [isTTSEnabled, setIsTTSEnabled] = useState(true);
  const [typingDelay, setTypingDelay] = useState(null);

  // Char param from URL
  const [charParam, setCharParam] = useState("");

  const [bcParam, setBcParam] = useState("");
  const [tcParam, setTcParam] = useState("");

  // Speech references
  const speechConfig = useRef(null);
  const audioConfig = useRef(null);
  const recognizer = useRef(null);
  const speechSynthesizer = useRef(null);

  // For voice-to-text
  const [isListening, setIsListening] = useState(false);

  // HeyGen peer connection
  const peerConnection = useRef(null);
  const sessionInfo = useRef(null);

  // TTS hover pause
  const ttsHoverPauseRef = useRef(false);
  const spokenHoverTextsRef = useRef(new Set());

  // Audio elements and controls for ElevenLabs
  const audioRef = useRef(new Audio());
  const isAudioPlayingRef = useRef(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioQueue = useRef([]);
  const isProcessingAudio = useRef(false);

  // Current message ref
  const currentMessageRef = useRef("");

  // Pronunciation dictionary
  const IYKYK_XML_DICTIONARY = `<?xml version="1.0" encoding="utf-8"?>
<lexicon version="1.0"
         xmlns="http://www.w3.org/2005/01/pronunciation-lexicon"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://www.w3.org/2005/01/pronunciation-lexicon
           http://www.w3.org/TR/2007/CR-pronunciation-lexicon-20071212/pls.xsd"
         alphabet="ipa" xml:lang="en-US">
  <lexeme>
    <grapheme>IYKYK</grapheme>
    <alias>eye why kay why kay</alias>
  </lexeme>
  <lexeme>
    <grapheme>iykyk</grapheme>
    <alias>eye why kay why kay</alias>
  </lexeme>
</lexicon>`;

  const iykykDictRef = { id: null, version_id: null };

  // HANDSHAKE PROTOCOL IMPLEMENTATION
  // HANDSHAKE PROTOCOL IMPLEMENTATION - REPLACE ENTIRE useEffect
  useEffect(() => {
    // Check for context parameter first
    const params = new URLSearchParams(location.search);
    const contextParam = params.get("context");
    const requiresContext = contextParam === "true";

    setContextRequired(requiresContext);

    if (!requiresContext) {
      // If context not required, set handshake as complete immediately
      console.log("🚫 Context not required - skipping handshake protocol");
      setHandshakeState({
        isReady: true,
        userDataReceived: false,
        handshakeComplete: true,
        isBlocked: false,
        errorMessage: null,
        loadingMessage: null,
      });
      return; // Exit early, no handshake needed
    }

    console.log("🤝 Setting up blocking handshake protocol...");

    // Initialize blocking state since context is required
    setHandshakeState((prev) => ({
      ...prev,
      isBlocked: true,
      loadingMessage: "Initializing AI Assistant...",
    }));

    // Handle initial message with user data
    const handleInitialMessageWithUserData = async (
      message,
      userData,
      userContext
    ) => {
      try {
        console.log("🚀 Processing initial message with user data...");

        // Store user data
        if (userData) {
          setUserAccountContext(userData);
          userContextRef.current = userData;
        }

        // Update handshake state
        setHandshakeState((prev) => ({
          ...prev,
          userDataReceived: true,
          loadingMessage: "Processing user account data...",
        }));

        // Send the initial message automatically
        if (message) {
          console.log("📤 Sending initial message to AI...");
          await handleSendMessageInternal(message, true); // true indicates system message

          // Send confirmation back to widget
          window.parent.postMessage(
            {
              type: "INITIAL_MESSAGE_RECEIVED",
              timestamp: new Date().toISOString(),
            },
            "*"
          );

          // Complete handshake
          setTimeout(() => {
            completeHandshake();
          }, 2000);
        }
      } catch (error) {
        console.error("❌ Error processing initial message:", error);
        setHandshakeState((prev) => ({
          ...prev,
          isBlocked: true,
          errorMessage:
            "Failed to process user data. Please refresh and try again.",
          loadingMessage: null,
        }));
      }
    };

    // Handle user data received
    const handleUserDataReceived = (data, userContext) => {
      console.log("📋 Storing user data for future messages");
      setTalentLmsData(data);
      talentLmsDataRef.current = data;

      if (userContext) {
        setUserAccountContext(userContext);
        userContextRef.current = userContext;
      }

      setHandshakeState((prev) => ({
        ...prev,
        userDataReceived: true,
      }));
    };

    // Complete handshake
    const completeHandshake = () => {
      console.log("🎉 Handshake complete - chat is now ready!");
      setHandshakeState({
        isReady: true,
        userDataReceived: true,
        handshakeComplete: true,
        isBlocked: false,
        errorMessage: null,
        loadingMessage: null,
      });

      // Send acknowledgment back to widget
      window.parent.postMessage(
        {
          type: "HANDSHAKE_ACK",
          timestamp: new Date().toISOString(),
        },
        "*"
      );
    };

    // Listen for messages from parent widget
    const handleWidgetMessages = (event) => {
      console.log("📨 Message received from widget:", event.data);

      const { type, data, message, userData, userContext } = event.data;

      switch (type) {
        case "SEND_INITIAL_MESSAGE":
          console.log("✅ Received initial message with user data");
          handleInitialMessageWithUserData(message, userData, userContext);
          break;

        case "TALENTLMS_USER_DATA":
          console.log("✅ Received TalentLMS user data");
          handleUserDataReceived(data, userContext);
          break;

        case "IFRAME_LOADED_FROM_WIDGET":
          console.log("✅ Widget confirmed iframe loaded");
          setHandshakeState((prev) => ({
            ...prev,
            loadingMessage: "Loading user account data...",
          }));
          break;

        case "HANDSHAKE_COMPLETE":
          console.log("✅ Handshake complete from widget");
          completeHandshake();
          break;

        case "HANDSHAKE_FAILED":
          console.log("❌ Handshake failed from widget");
          setHandshakeState((prev) => ({
            ...prev,
            isBlocked: true,
            errorMessage:
              "Failed to load user data. Please refresh and try again.",
            loadingMessage: null,
          }));
          break;
      }
    };

    // Add event listener
    window.addEventListener("message", handleWidgetMessages);

    // Signal that chatbot is ready
    setTimeout(() => {
      console.log("📡 Sending CHATBOT_READY signal to widget");
      window.parent.postMessage(
        {
          type: "CHATBOT_READY",
          timestamp: new Date().toISOString(),
        },
        "*"
      );
    }, 1000);

    // Send iframe loaded signal
    setTimeout(() => {
      console.log("📡 Sending CHATBOT_IFRAME_LOADED signal to widget");
      window.parent.postMessage(
        {
          type: "CHATBOT_IFRAME_LOADED",
          timestamp: new Date().toISOString(),
        },
        "*"
      );
    }, 1500);

    // Cleanup
    return () => {
      window.removeEventListener("message", handleWidgetMessages);
    };
  }, [location]); // Add location as dependency

  // Handle user data received
  const handleUserDataReceived = (data, userContext) => {
    console.log("📋 Storing user data for future messages");
    setTalentLmsData(data);
    talentLmsDataRef.current = data;

    if (userContext) {
      setUserAccountContext(userContext);
      userContextRef.current = userContext;
    }

    setHandshakeState((prev) => ({
      ...prev,
      userDataReceived: true,
    }));
  };

  // Complete handshake
  const completeHandshake = () => {
    console.log("🎉 Handshake complete - chat is now ready!");
    setHandshakeState({
      isReady: true,
      userDataReceived: true,
      handshakeComplete: true,
      isBlocked: false,
      errorMessage: null,
      loadingMessage: null,
    });

    // Send acknowledgment back to widget
    window.parent.postMessage(
      {
        type: "HANDSHAKE_ACK",
        timestamp: new Date().toISOString(),
      },
      "*"
    );
  };

  // Enhanced message sending with user data appending - REPLACE ENTIRE FUNCTION
  // Enhanced message sending with user data appending - REPLACE ENTIRE FUNCTION
  const appendUserDataToMessage = (message) => {
    // Only append user data if context is required
    if (!contextRequired) {
      console.log("⚠️ Context not required - skipping user data append");
      return message;
    }

    // Don't append to system messages that already have user data
    if (message.includes("[SYSTEM - USER ACCOUNT DATA")) {
      return message;
    }

    // If we have user context, append it to every message
    if (userContextRef.current) {
      const timestamp = new Date().toISOString();
      const enhancedMessage = `${message}

[USER_CONTEXT - AUTOMATICALLY_APPENDED]:
${JSON.stringify(userContextRef.current, null, 2)}

[MESSAGE_METADATA]:
- Timestamp: ${timestamp}
- Session: ${session_id}
- Class: ${class_id}

[INSTRUCTIONS]: Use the user context above to provide personalized responses when relevant. This data is automatically appended to every message for context.`;

      console.log("✅ User data appended to message");
      return enhancedMessage;
    }

    console.log("⚠️ No user data to append");
    return message;
  };

  // Block message sending if handshake not complete - REPLACE ENTIRE FUNCTION
  const handleSendMessage = async (msg = question) => {
    // Only block if context is required AND handshake not complete
    if (
      contextRequired &&
      (handshakeState.isBlocked || !handshakeState.handshakeComplete)
    ) {
      console.log("🚫 Message blocked - handshake not complete");

      // Request user data if not received
      window.parent.postMessage(
        {
          type: "REQUEST_USER_DATA",
          timestamp: new Date().toISOString(),
        },
        "*"
      );

      // Show blocking message
      setHandshakeState((prev) => ({
        ...prev,
        loadingMessage: "Waiting for user account data...",
      }));

      return;
    }

    return handleSendMessageInternal(msg, false);
  };

  // Internal message handling with user data appending - MOVE THIS BEFORE THE HANDSHAKE useEffect
  const handleSendMessageInternal = async (
    msg = question,
    isSystemMessage = false
  ) => {
    const messageToSend = typeof msg === "string" ? msg : String(msg);
    if (!messageToSend || isLoading || isTyping) {
      return;
    }

    const timestamp = new Date().toLocaleString();
    let messageWithTimestamp = `${messageToSend} (Sent at: ${timestamp})`;

    // Append user data to every message (except system messages with user data already)
    if (!isSystemMessage) {
      messageWithTimestamp = appendUserDataToMessage(messageWithTimestamp);
    }

    setQuestionList([...questionList, messageToSend]);
    timestampedQuestions.current.push(messageWithTimestamp);

    let payload = {
      question: messageWithTimestamp,
      modelId: activeModel,
      session_id,
      class_id,
      isBubble: true,
    };

    // Add TalentLMS context to payload
    if (talentLmsDataRef.current) {
      payload.talentLmsContext = {
        hasUserData: true,
        userId:
          talentLmsDataRef.current.user?.id ||
          talentLmsDataRef.current._data?.id,
        username:
          talentLmsDataRef.current.user?.login ||
          talentLmsDataRef.current._data?.login,
        currentCourse: getCurrentCourseFromUrl(),
        timestamp: new Date().toISOString(),
      };

      console.log(
        "📤 Sending message with TalentLMS context and appended user data"
      );
    }

    if (!activeChat.id) {
      const splitQues = messageToSend.split(" ");
      payload.isNew = true;
      payload.title =
        splitQues[0] + " " + (splitQues[1] || "") + " " + (splitQues[2] || "");
    } else {
      payload.id = activeChat.id;
    }

    // Add style flags
    if (isLMSQuizStyle) {
      payload.isLMSQuiz = true;
    }
    if (isLMSActivityStyle) {
      payload.isLMSActivity = true;
    }

    setIsLoading(true);
    if (!isSystemMessage) {
      setQuestion("");
    }

    try {
      // Send to API
      const res = await generateBubbleChat(payload);
      setQueries(res.data.chats);

      const oldActiveChat = res.data.chats.find(
        (chat) => chat.id === activeChat.id
      );
      const chat = oldActiveChat
        ? oldActiveChat
        : res.data.chats[res.data.chats.length - 1];
      setActiveChat(chat);

      setIsLoading(false);

      // Handle the new message
      const latestMessageIndex = chat.queries.length - 1;
      if (latestMessageIndex >= 0) {
        const latestMessage = chat.queries[latestMessageIndex].solution;

        if (latestMessage) {
          // Store the current message in ref FIRST
          currentMessageRef.current = latestMessage;

          // Reset typing states
          setSkipTyping(false);
          setIsTyping(false);
          setTypingIndex(0);
          setDisplayedText("");
          setTypingDelay(null);

          // For system messages, show response immediately without TTS
          if (isSystemMessage) {
            console.log("🤫 Skipping TTS for system message");
            setDisplayedText(latestMessage);
            setTypingIndex(latestMessage.length);
          } else {
            // Regular message with TTS
            setTimeout(async () => {
              try {
                await textToSpeech(latestMessage);
              } catch (err) {
                console.error("Error during TTS:", err);
                setTypingDelay(30);
                setIsTyping(true);
              }
            }, 10);
          }
        }
      }
    } catch (err) {
      console.error("Error sending message:", err);
      toast("Something went wrong. Please check retrain model status", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setIsLoading(false);
    }
  };
  // Helper function to get current course from URL
  const getCurrentCourseFromUrl = () => {
    const urlParts = window.location.pathname.split("/");
    const courseIndex = urlParts.indexOf("courses");
    if (courseIndex !== -1 && urlParts[courseIndex + 1]) {
      return urlParts[courseIndex + 1];
    }
    return null;
  };

  // Function to detect if question is about user account
  const isAccountRelatedQuestion = (question) => {
    const accountKeywords = [
      "my account",
      "my profile",
      "my progress",
      "my courses",
      "my grades",
      "my enrollment",
      "my completion",
      "my certificates",
      "my time spent",
      "how many courses",
      "what courses",
      "my learning",
      "my achievements",
      "my status",
      "my performance",
      "course progress",
      "learning progress",
      "completed courses",
      "enrolled courses",
      "my dashboard",
      "my stats",
    ];

    const questionLower = question.toLowerCase();
    return accountKeywords.some((keyword) => questionLower.includes(keyword));
  };

  // Initialize component
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startingMessage = params.get("starting_message");
    const styleParam = params.get("style");
    const charFromUrl = params.get("char") || "";

    const bcFromUrl = params.get("bc") || "";
    const tcFromUrl = params.get("tc") || "";

    setBcParam(bcFromUrl);
    setTcParam(tcFromUrl);

    setCharParam(charFromUrl);

    if (startingMessage) {
      setInitialMessage(startingMessage);
      setTimeout(() => {
        setInitialMessage("");
      }, 0);
    }

    // Style checks

    if (styleParam === "iykykfast" || styleParam === "fast") {
      setIsIYKYKFastStyle(true);
      setIsOpen(true);
      setIsTTSEnabled(false); // absolutely no TTS in fast style
    }
    if (styleParam === "lms") {
      setIsLMSStyle(true);
      setIsOpen(true);
    }
    if (styleParam === "heygen") {
      setIsHeygenStyle(true);
      setIsOpen(true);
      initializeSession();
    }
    if (styleParam === "lmsquiz") {
      setIsLMSQuizStyle(true);
      setIsOpen(true);
    }
    if (styleParam === "lmsquiz2") {
      setIsLMSQuizStyle2(true);
      setIsOpen(true);
    }
    if (styleParam === "lmsquiz3") {
      setIsLMSQuizStyle3(true);
      setIsOpen(true);
    }
    if (styleParam === "lmsactivity") {
      setIsLMSActivityStyle(true);
      setIsOpen(true);
    }
    if (styleParam === "wave") {
      setIsWaveStyle(true);
      setIsOpen(true);
    }

    // Add this check alongside the other style checks
    if (styleParam === "hybrid" || styleParam === "hybridfast") {
      setIsHybridStyle(true);
      setIsOpen(true);
      setIsTTSEnabled(false); // Disable TTS for fast performance
    }

    // Initialize voice only if NOT fast style
    if (!isIYKYKFastStyle) {
      speechConfig.current = sdk.SpeechConfig.fromSubscription(
        SPEECH_KEY,
        SPEECH_REGION
      );
      speechConfig.current.speechRecognitionLanguage = "en-US";
      audioConfig.current = sdk.AudioConfig.fromDefaultMicrophoneInput();
      recognizer.current = new sdk.SpeechRecognizer(
        speechConfig.current,
        audioConfig.current
      );

      // Recognizer events
      recognizer.current.recognized = (s, e) => processRecognizedTranscript(e);
      recognizer.current.recognizing = (s, e) =>
        processRecognizingTranscript(e);
    }

    (async function initIykykDict() {
      // Skip completely for the fast style
      if (iykykDictRef.id || isIYKYKFastStyle) return;

      const createRes = await fetch(
        `${ELEVENLABS_API_URL}/pronunciation-dictionaries/add-from-file`,
        {
          method: "POST",
          headers: {
            "xi-api-key": ELEVENLABS_API_KEY,
          },
          body: (() => {
            const fd = new FormData();
            fd.append("name", "iykyk-inline");
            fd.append(
              "file",
              new Blob([IYKYK_XML_DICTIONARY], { type: "text/xml" }),
              "iykyk.pls"
            );
            return fd;
          })(),
        }
      );

      if (!createRes.ok) {
        console.error("Could not create IYKYK dict", await createRes.text());
        return;
      }

      const { id: dictId, version_id } = await createRes.json();
      iykykDictRef.id = dictId;
      iykykDictRef.version_id = version_id;
      console.log("IYKYK dict ready →", iykykDictRef);
    })();

    return () => {
      // Cleanup
      if (recognizer.current) {
        recognizer.current.stopContinuousRecognitionAsync(() => {
          setIsListening(false);
        });
      }

      cleanupAudioHandlers();

      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.src = "";
      }

      audioQueue.current = [];
      isProcessingAudio.current = false;
    };
  }, [location]);

  // Cleanup audio handlers
  const cleanupAudioHandlers = () => {
    if (audioRef.current) {
      audioRef.current.onloadedmetadata = null;
      audioRef.current.onplay = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.oncanplay = null;
    }
  };

  // Speech recognition functions
  const processRecognizedTranscript = (event) => {
    const result = event.result;
    if (result.reason === sdk.ResultReason.RecognizedSpeech) {
      const transcript = result.text.trim();
      setQuestion((prevQuestion) =>
        prevQuestion ? `${prevQuestion} ${transcript}` : transcript
      );
    }
  };

  const processRecognizingTranscript = (event) => {
    const result = event.result;
    if (result.reason === sdk.ResultReason.RecognizingSpeech) {
      const transcript = result.text.trim();
    }
  };

  const handleVoiceInput = () => {
    // Only block voice input if context is required and handshake not complete
    if (
      contextRequired &&
      (handshakeState.isBlocked || !handshakeState.handshakeComplete)
    ) {
      console.log("🚫 Voice input blocked - handshake not complete");
      return;
    }

    if (isListening) {
      recognizer.current.stopContinuousRecognitionAsync(() => {
        console.log("Speech recognition stopped.");
        setIsListening(false);
      });
    } else {
      recognizer.current.startContinuousRecognitionAsync(() => {
        console.log("Speech recognition started.");
        setIsListening(true);
      });
    }
  };

  const stopSpeechRecognition = (reason) => {
    if (isListening) {
      recognizer.current.stopContinuousRecognitionAsync(() => {
        console.log(`Speech recognition stopped due to ${reason}.`);
        setIsListening(false);
      });
    }
  };

  // HeyGen session functions (keeping existing implementation)
  const initializeSession = async () => {
    try {
      console.log("Initializing session...");
      sessionInfo.current = await createNewSession();
      await startAndDisplaySession();
    } catch (error) {
      console.error("Error initializing HeyGen session:", error);
    }
  };

  const createNewSession = async () => {
    const response = await fetch(`${HEYGEN_SERVER_URL}/v1/streaming.new`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": HEYGEN_API_KEY,
      },
      body: JSON.stringify({
        quality: "low",
        avatar_name: HEYGEN_AVATAR_ID,
        voice: { voice_id: HEYGEN_VOICE_ID },
      }),
    });

    if (response.status !== 200) {
      throw new Error("Failed to create session");
    }
    const data = await response.json();
    return data.data;
  };

  const startAndDisplaySession = async () => {
    if (!sessionInfo.current) {
      return;
    }

    const { sdp: serverSdp, ice_servers2: iceServers } = sessionInfo.current;
    peerConnection.current = new RTCPeerConnection({ iceServers });

    peerConnection.current.ontrack = (event) => {
      if (event.track.kind === "audio" || event.track.kind === "video") {
        const mediaElement = document.querySelector("#mediaElement");
        if (mediaElement) {
          mediaElement.srcObject = event.streams[0];
        }
      }
    };

    peerConnection.current.onicecandidate = ({ candidate }) => {
      if (candidate) {
        handleICE(sessionInfo.current.session_id, candidate.toJSON());
      }
    };

    const remoteDescription = new RTCSessionDescription(serverSdp);
    await peerConnection.current.setRemoteDescription(remoteDescription);

    const localDescription = await peerConnection.current.createAnswer();
    await peerConnection.current.setLocalDescription(localDescription);

    await startSession(sessionInfo.current.session_id, localDescription);
  };

  const handleICE = async (session_id, candidate) => {
    const response = await fetch(`${HEYGEN_SERVER_URL}/v1/streaming.ice`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": HEYGEN_API_KEY,
      },
      body: JSON.stringify({ session_id, candidate }),
    });

    if (response.status !== 200) {
      throw new Error("Failed to handle ICE");
    }
  };

  const startSession = async (session_id, sdp) => {
    const response = await fetch(`${HEYGEN_SERVER_URL}/v1/streaming.start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": HEYGEN_API_KEY,
      },
      body: JSON.stringify({ session_id, sdp }),
    });

    if (response.status !== 200) {
      throw new Error("Failed to start session");
    }
  };

  // HeyGen TTS
  const heyGenTTS = async (text) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${HEYGEN_SERVER_URL}/v1/streaming.task`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": HEYGEN_API_KEY,
          },
          body: JSON.stringify({
            session_id: sessionInfo.current.session_id,
            text: text,
          }),
        });

        if (response.status !== 200) {
          reject(new Error("Failed to process TTS"));
          return;
        }
        setTimeout(() => {
          resolve();
        }, 1000);
      } catch (error) {
        console.error("Error in HeyGen TTS:", error);
        reject(error);
      }
    });
  };

  // ElevenLabs TTS function (keeping existing implementation)
  const elevenLabsTTS = async (text) => {
    return new Promise(async (resolve, reject) => {
      try {
        let filtered_text;
        if (
          isLMSActivityStyle &&
          charParam !== "gold" &&
          charParam !== "gold"
        ) {
          filtered_text = text.replace(/#/g, "").replace(/\d+\)\s*/g, "");
        } else {
          filtered_text = text.replace(/#/g, "");
        }

        let voiceId = ELEVENLABS_VOICE_MAP.default;
        if (charParam && ELEVENLABS_VOICE_MAP[charParam]) {
          voiceId = ELEVENLABS_VOICE_MAP[charParam];
        }

        let voiceSettings = {
          stability: 0.3,
          similarity_boost: 0.5,
          style: 1.0,
          use_speaker_boost: true,
        };

        const streamParams = {
          output_format: "mp3_44100_128",
          optimize_streaming_latency: 4,
          model: ELEVENLABS_MODEL,
        };

        const response = await fetch(
          `${ELEVENLABS_API_URL}/text-to-speech/${voiceId}/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "xi-api-key": ELEVENLABS_API_KEY,
            },
            body: JSON.stringify({
              text: filtered_text,
              model_id: ELEVENLABS_MODEL,
              pronunciation_dictionary_locators: iykykDictRef.id
                ? [{ id: iykykDictRef.id, version_id: iykykDictRef.version_id }]
                : [],
              voice_settings: voiceSettings,
              ...streamParams,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("ElevenLabs API error:", errorData);
          reject(new Error(`Failed to process TTS: ${response.status}`));
          return;
        }

        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        const audioElement = audioRef.current;
        cleanupAudioHandlers();

        let hasResolved = false;
        const resolveOnce = () => {
          if (!hasResolved) {
            hasResolved = true;
            resolve();
          }
        };

        audioElement.onloadedmetadata = () => {
          const audioDurationMs = audioElement.duration * 1000;
          const originalTextLength = text.length;
          const calculatedDelay = (audioDurationMs * 0.95) / text.length;
          const boundedDelay = Math.min(Math.max(calculatedDelay, 25), 150);

          console.log(
            `Audio duration: ${audioDurationMs}ms, Text length: ${originalTextLength}, Delay: ${boundedDelay}ms`
          );
          setTypingDelay(boundedDelay);
        };

        audioElement.oncanplay = () => {
          console.log("Audio ready to play");
        };

        audioElement.onplay = () => {
          isAudioPlayingRef.current = true;
          setIsAudioPlaying(true);
          setTypingDelay((prev) => prev || 30);
          setIsTyping(true);
          console.log("Audio started playing, typing started");
        };

        audioElement.onended = () => {
          isAudioPlayingRef.current = false;
          setIsAudioPlaying(false);
          setIsTyping(false);

          const currentMessage = currentMessageRef.current;
          if (currentMessage) {
            setDisplayedText(currentMessage);
            setTypingIndex(currentMessage.length);
          }

          console.log("Audio ended, typing completed");
          URL.revokeObjectURL(audioUrl);

          if (audioQueue.current.length > 0) {
            processAudioQueue();
          }

          resolveOnce();
        };

        audioElement.onerror = (error) => {
          console.error("Audio playback failed:", error);
          cleanupAudioHandlers();
          isAudioPlayingRef.current = false;
          setIsAudioPlaying(false);
          setIsTyping(false);
          URL.revokeObjectURL(audioUrl);
          reject(error);
        };

        audioElement.src = audioUrl;
        audioElement.playbackRate = 1.0;
        audioElement.load();

        const playPromise = audioElement.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.error("Audio playback failed:", err);
            cleanupAudioHandlers();
            isAudioPlayingRef.current = false;
            setIsAudioPlaying(false);
            setIsTyping(false);
            URL.revokeObjectURL(audioUrl);
            reject(err);
          });
        }
      } catch (error) {
        console.error("Error in ElevenLabs TTS:", error);
        reject(error);
      }
    });
  };

  // Text to speech function
  const textToSpeech = async (text) => {
    handleStopTTS();

    return new Promise((resolve, reject) => {
      if (!isTTSEnabled || !text || text.trim() === "") {
        setTypingDelay(30);
        setIsTyping(true);
        resolve();
        return;
      }

      if (isHeygenStyle) {
        heyGenTTS(text)
          .then(() => {
            setTypingDelay(30);
            setIsTyping(true);
            resolve();
          })
          .catch(reject);
        return;
      }

      elevenLabsTTS(text)
        .then(resolve)
        .catch((err) => {
          console.error("ElevenLabs TTS failed:", err);
          setTypingDelay(30);
          setIsTyping(true);
          resolve();
        });
    });
  };

  // Typewriter effect
  useEffect(() => {
    const currentMessage = currentMessageRef.current;

    if (!currentMessage || skipTyping) {
      setIsTyping(false);
      return;
    }

    if (typingIndex >= currentMessage.length) {
      setIsTyping(false);
      return;
    }

    if (!isTyping || typingDelay === null) {
      return;
    }

    const timer = setTimeout(() => {
      setDisplayedText(currentMessage.slice(0, typingIndex + 1));
      setTypingIndex(typingIndex + 1);
    }, typingDelay);

    return () => clearTimeout(timer);
  }, [typingIndex, skipTyping, typingDelay, isTyping]);

  // Stop TTS function
  const handleStopTTS = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        cleanupAudioHandlers();

        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }

        audioRef.current.src = "";
      } catch (e) {
        console.error("Error stopping audio:", e);
      }
    }

    isAudioPlayingRef.current = false;
    setIsAudioPlaying(false);
    setIsTyping(false);
    audioQueue.current = [];
    isProcessingAudio.current = false;
  };

  // Skip typing function
  const handleSkipTyping = () => {
    handleStopTTS();

    const currentMessage = currentMessageRef.current;

    if (currentMessage) {
      setDisplayedText(currentMessage);
      setTypingIndex(currentMessage.length);
    }

    setIsTyping(false);
    setSkipTyping(true);
    setTypingDelay(null);
  };

  // Audio queue processing
  const processAudioQueue = async () => {
    if (isProcessingAudio.current || audioQueue.current.length === 0) {
      return;
    }

    isProcessingAudio.current = true;
    setIsAudioPlaying(true);

    const nextAudio = audioQueue.current.shift();
    try {
      handleStopTTS();
      await textToSpeech(nextAudio.text);
    } catch (error) {
      console.error("Error processing audio in queue:", error);
    } finally {
      isProcessingAudio.current = false;
      setIsAudioPlaying(false);

      if (audioQueue.current.length > 0) {
        processAudioQueue();
      }
    }
  };

  // Speak hover text function
  const speakHoverText = (hoverText) => {
    if (charParam === "gold" || charParam === "kel" || !isTTSEnabled) return;
    if (!hoverText || hoverText.trim() === "") return;

    handleStopTTS();

    if (isTyping) return;

    if (!ttsHoverPauseRef.current) {
      handleStopTTS();
      textToSpeech(hoverText);
      spokenHoverTextsRef.current.add(hoverText);

      ttsHoverPauseRef.current = true;
      setTimeout(() => {
        ttsHoverPauseRef.current = false;
      }, 800);
    }
  };

  // Input handling functions
  const handleInputChange = (input) => {
    // Only block input if context is required and handshake not complete
    if (
      contextRequired &&
      (handshakeState.isBlocked || !handshakeState.handshakeComplete)
    ) {
      console.log("🚫 Input blocked - handshake not complete");
      return;
    }

    const newValue =
      typeof input === "string" ? input : input?.target?.value || "";
    setQuestion(newValue);
  };

  const handleKeyDown = (event) => {
    // Only block key input if context is required and handshake not complete
    if (
      contextRequired &&
      (handshakeState.isBlocked || !handshakeState.handshakeComplete)
    ) {
      console.log("🚫 Key input blocked - handshake not complete");
      event.preventDefault();
      return;
    }

    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleBubbleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleInputFocus = () => {
    stopSpeechRecognition("input focus");
  };

  // Common props for child style components
  const getCommonProps = () => ({
    displayedText,
    initialMessage,
    question,
    handleVoiceInput,
    isListening,
    inputRef: null,
    handleInputChange,
    handleKeyDown,
    handleInputFocus,
    handleSendMessage,
    isLoading,
    isTyping,
    handleSkipTyping,
    messagesContainerRef,
    setQuestion,
    talentLmsData,
    userAccountContext,
    isAccountRelatedQuestion,
    // NEW: Add handshake state props
    handshakeState,
  });

  // RENDER BLOCKING UI IF HANDSHAKE NOT COMPLETE - REPLACE ENTIRE CONDITION
  // RENDER BLOCKING UI IF HANDSHAKE NOT COMPLETE - REPLACE ENTIRE CONDITION
  if (
    contextRequired &&
    (handshakeState.isBlocked || !handshakeState.handshakeComplete)
  ) {
    const BlockingUI = ({ message, isError = false }) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          color: isError ? "#dc3545" : "#6c757d",
          textAlign: "center",
        }}
      >
        {!isError && (
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e9ecef",
              borderTop: "4px solid #0F61A4",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "16px",
            }}
          ></div>
        )}
        <div
          style={{
            fontSize: "16px",
            fontWeight: "500",
            marginBottom: "8px",
          }}
        >
          {isError ? "⚠️ Error" : "🤖 AI Assistant"}
        </div>
        <div
          style={{
            fontSize: "14px",
            opacity: 0.8,
          }}
        >
          {message}
        </div>
        {isError && (
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              backgroundColor: "#0F61A4",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Refresh Page
          </button>
        )}
        <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    );

    return (
      <BlockingUI
        message={
          handshakeState.errorMessage ||
          handshakeState.loadingMessage ||
          "Preparing AI Assistant..."
        }
        isError={!!handshakeState.errorMessage}
      />
    );
  }

  // Render based on style (once handshake is complete)
  if (isLMSStyle) {
    return (
      <LMSStyle
        {...getCommonProps()}
        session_id={session_id}
        class_id={class_id}
        isTTSEnabled={isTTSEnabled}
        setIsTTSEnabled={setIsTTSEnabled}
        handleStopTTS={handleStopTTS}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        avatar={avatar}
        serverAddress={serverAddress}
        char={charParam}
        bc={bcParam}
        tc={tcParam}
      />
    );
  } else if (isHeygenStyle) {
    return <HeygenStyle {...getCommonProps()} />;
  } else if (isLMSQuizStyle) {
    return (
      <LMSQuizStyle
        {...getCommonProps()}
        speakHoverText={speakHoverText}
        session_id={session_id}
        class_id={class_id}
        isTTSEnabled={isTTSEnabled}
        setIsTTSEnabled={setIsTTSEnabled}
        handleStopTTS={handleStopTTS}
        char={charParam}
        questionList={questionList}
        activeChat={activeChat}
        timestampedQuestions={timestampedQuestions}
      />
    );
  } else if (isLMSActivityStyle) {
    return (
      <LMSActivityStyle
        {...getCommonProps()}
        speakHoverText={speakHoverText}
        session_id={session_id}
        class_id={class_id}
        isTTSEnabled={isTTSEnabled}
        setIsTTSEnabled={setIsTTSEnabled}
        handleStopTTS={handleStopTTS}
        char={charParam}
        questionList={questionList}
        activeChat={activeChat}
        timestampedQuestions={timestampedQuestions}
        bc={bcParam}
        tc={tcParam}
      />
    );
  } else if (isWaveStyle) {
    return (
      <WaveStyle
        {...getCommonProps()}
        session_id={session_id}
        class_id={class_id}
        isTTSEnabled={isTTSEnabled}
        setIsTTSEnabled={setIsTTSEnabled}
        handleStopTTS={handleStopTTS}
        char={charParam}
        questionList={questionList}
        activeChat={activeChat}
        timestampedQuestions={timestampedQuestions}
        isAudioPlaying={isAudioPlaying}
        isAudioPlayingRef={isAudioPlayingRef}
      />
    );
  } else if (isIYKYKFastStyle) {
    // Single-file inline style component (no avatar, no TTS, no mic)
    return (
      <IYKYKFastInline
        {...getCommonProps()}
        displayedText={displayedText}
        initialMessage="Welcome! How can I assist you today?"
        question={question}
        handleInputChange={handleInputChange}
        handleKeyDown={handleKeyDown}
        handleSendMessage={handleSendMessage}
        isLoading={isLoading}
        isTyping={isTyping}
        handleSkipTyping={handleSkipTyping}
        messagesContainerRef={messagesContainerRef}
      />
    );
  } else if (isHybridStyle) {
    // Hybrid style with "Get Started" button
    return (
      <HybridChatInterface
        {...getCommonProps()}
        displayedText={displayedText}
        initialMessage={
          initialMessage ||
          "Message from System: If the user hasn't started any of the questions yet please greet them and immediately start the lesson, otherwise tell them welcome back and get back into the lesson"
        }
        question={question}
        handleInputChange={handleInputChange}
        handleKeyDown={handleKeyDown}
        handleSendMessage={handleSendMessage}
        isLoading={isLoading}
        isTyping={isTyping}
        handleSkipTyping={handleSkipTyping}
        messagesContainerRef={messagesContainerRef}
        conversationHistory={[]} // Empty initially, will be populated as messages are sent
        handleVoiceInput={handleVoiceInput}
        isListening={isListening}
      />
    );
  } else {
    // Default style
    return (
      <DefaultStyle
        isOpen={isOpen}
        avatar={avatar}
        serverAddress={serverAddress}
        questionList={questionList}
        activeChat={activeChat}
        timestampedQuestions={timestampedQuestions}
        isLoading={isLoading}
        question={question}
        handleInputChange={handleInputChange}
        handleKeyDown={handleKeyDown}
        handleSendMessage={handleSendMessage}
        handleBubbleClick={handleBubbleClick}
        messagesContainerRef={messagesContainerRef}
      />
    );
  }
}

export default BubbleChat;
